import React from 'react';
import { ChevronRight, Inbox, ArrowUpRight, Target, TrendingUp, Zap, UsersRound } from 'lucide-react';
import { Avatar, Badge, EmptyState } from '../components/ui.jsx';
import RevenueChart from '../components/RevenueChart.jsx';
import LeadsPanel from '../components/LeadsPanel.jsx';
import RetentionPanel from '../components/RetentionPanel.jsx';
import CalendarWidget from '../components/CalendarWidget.jsx';
import LocationsMap from '../components/LocationsMap.jsx';
import { money, userById, stageColor } from '../data.js';

export default function Dashboard({ deals = [], partners = [], accounts = [], tasks = [], team = [], onOpenDeal, onNavigate, currentUser }) {
  const totalLeads = deals.length;
  const won = deals.filter((d) => d.stage === 'Won').length;
  const lost = deals.filter((d) => d.stage === 'Lost').length;
  const closed = won + lost;
  const conversion = closed ? Math.round((won / closed) * 100) : 0;
  const openDeals = deals.filter((d) => !['Won', 'Lost'].includes(d.stage));
  const pipelineValue = openDeals.reduce((sum, deal) => sum + deal.value, 0);
  const weightedForecast = openDeals.reduce((sum, deal) => sum + (deal.value * (deal.probability || 0) / 100), 0);
  const target = 2000000;
  const targetProgress = Math.min(100, Math.round((pipelineValue / target) * 100));
  const priorityDeal = [...openDeals].sort((a, b) => (b.value * (b.probability || 0)) - (a.value * (a.probability || 0)))[0];
  const todayLabel = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <>
      <section className="command-hero">
        <div className="command-hero-glow command-hero-glow--one" />
        <div className="command-hero-glow command-hero-glow--two" />
        <div className="command-copy">
          <div className="command-eyebrow"><span className="command-live-dot" /> Revenue command center <span>{todayLabel}</span></div>
          <h1>Good morning, {currentUser?.name?.split(' ')[0] || 'Prem'}.</h1>
          <p>Your pipeline is moving. Here’s the signal behind today’s revenue motion.</p>
          <div className="command-value-label">Open pipeline</div>
          <div className="command-value">{money(pipelineValue)}</div>
          <div className="command-progress-row"><div className="command-progress"><span style={{ width: `${targetProgress}%` }} /></div><b>{targetProgress}%</b><small>of quarterly target</small></div>
          <div className="command-actions">
            <button className="command-primary" onClick={() => onNavigate('leads')}>Explore pipeline <ArrowUpRight size={15} /></button>
          </div>
        </div>
        <div className="command-insight">
          <div className="command-orbit">
            <svg viewBox="0 0 180 180"><circle className="orbit-track" cx="90" cy="90" r="68"/><circle className="orbit-progress" cx="90" cy="90" r="68" style={{ strokeDashoffset: 427 - (427 * targetProgress / 100) }}/></svg>
            <div><strong>{targetProgress}%</strong><span>target coverage</span></div>
          </div>
          <div className="command-insight-card">
            <div className="command-insight-icon"><Zap size={15} /></div>
            <div><span>Next best opportunity</span><b>{priorityDeal?.account || 'Build your pipeline'}</b><small>{priorityDeal ? `${money(priorityDeal.value)} · ${priorityDeal.probability || 0}% confidence` : 'Add a deal to get started'}</small></div>
            {priorityDeal && <button onClick={() => onOpenDeal(priorityDeal)}><ChevronRight size={16} /></button>}
          </div>
        </div>
      </section>

      <section className="dash-top-row">
        <div className="mini-stat">
          <div className="mini-stat-top"><span className="mini-stat-icon mini-stat-icon--blue"><TrendingUp size={16}/></span><span className="mini-stat-pulse">Live</span></div>
          <div className="mini-stat-label">Weighted forecast</div><div className="mini-stat-row"><span className="mini-stat-value">{money(weightedForecast)}</span></div><div className="mini-stat-foot">Probability-adjusted pipeline</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-top"><span className="mini-stat-icon mini-stat-icon--violet"><Target size={16}/></span><span className="mini-stat-trend">↗ healthy</span></div>
          <div className="mini-stat-label">Win rate</div><div className="mini-stat-row"><span className="mini-stat-value">{closed ? `${conversion}%` : '—'}</span></div><div className="mini-stat-foot">Across {closed} closed opportunities</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-top"><span className="mini-stat-icon mini-stat-icon--cyan"><UsersRound size={16}/></span><span className="mini-stat-trend">{openDeals.length} active</span></div>
          <div className="mini-stat-label">Revenue motion</div><div className="mini-stat-row"><span className="mini-stat-value">{totalLeads}</span><span className="mini-stat-unit">opportunities</span></div><div className="mini-stat-foot">Across {partners.length} channel partners</div>
        </div>
      </section>

      <div className="dashboard-workspace">
        <div className="dashboard-primary-grid">
          <RevenueChart />
          <LocationsMap accounts={accounts} />
          <CalendarWidget tasks={tasks} accounts={accounts} partners={partners} />
        </div>

        <div className="dashboard-insight-grid">
          <LeadsPanel deals={deals} />
          <RetentionPanel partners={partners} />
        </div>

        <div className="card dashboard-opportunities">
            <div className="card-header">
              <div><div className="card-title">Active opportunities</div><div className="card-subtitle">All open deals across the pipeline</div></div>
              <button className="btn btn-ghost" onClick={() => onNavigate('leads')}>View all <ChevronRight size={14} /></button>
            </div>
            {openDeals.length === 0 ? (
              <EmptyState icon={Inbox} title="No open deals yet" desc="Deals you create in Leads & Pipeline will show up here." />
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>Company</th><th>Product</th><th>Stage</th><th>Value</th><th>Close date</th><th>Owner</th></tr></thead>
                  <tbody>
                    {openDeals.map((d) => (
                      <tr key={d.id} className="clickable" onClick={() => onOpenDeal(d)}>
                        <td>
                          <div className="row-entity">
                            <Avatar name={d.account} color={d.tone} size="sm" />
                            <div><div className="row-entity-name">{d.account}</div><div className="row-entity-sub">{d.contact}</div></div>
                          </div>
                        </td>
                        <td>{(d.products || []).join(', ')}</td>
                        <td><Badge tone={stageColor[d.stage]}>{d.stage}</Badge></td>
                        <td className="td-mono">{money(d.value)}</td>
                        <td className="td-muted">{new Date(d.close).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                        <td><Avatar name={userById(team, d.owner)?.name} color={userById(team, d.owner)?.color} size="sm" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>
    </>
  );
}
